import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { supabase, formatSupabaseError } from "@/lib/supabase";

/**
 * Handles the Supabase password recovery flow.
 *
 * Triggered when the user clicks the reset-password link in their email.
 * Supabase places either `?code=...` (PKCE) or `?token_hash=...&type=recovery`
 * (legacy / template-customised) in the URL — we handle both.
 *
 * Required Supabase Dashboard config (see /app/SUPABASE_AUTH_SETUP.md):
 *   - Auth → URL Configuration → Redirect URLs must include this page's URL
 *     on every host that ships the site (prod + preview).
 */
export default function AdminResetPassword() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("verifying"); // verifying | ready | submitting | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    if (!supabase) {
      setPhase("error");
      setErrorMsg("Supabase is not configured.");
      return;
    }

    let cancelled = false;

    async function init() {
      // 1) New PKCE flow: ?code=...
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (cancelled) return;
        if (error) { setErrorMsg(formatSupabaseError(error)); setPhase("error"); return; }
        // strip code from URL for cleanliness
        window.history.replaceState({}, "", "/admin/reset-password");
        setPhase("ready");
        return;
      }

      // 2) Hash-token flow (#access_token=...&type=recovery)
      //    supabase-js auto-detects this via detectSessionInUrl=true.
      //    Either we already have a session, or PASSWORD_RECOVERY fires shortly.
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) { setPhase("ready"); return; }

      // 3) Template-customised flow: ?token_hash=...&type=recovery
      const tokenHash = params.get("token_hash");
      const type = params.get("type");
      if (tokenHash && type === "recovery") {
        const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "recovery" });
        if (cancelled) return;
        if (error) { setErrorMsg(formatSupabaseError(error)); setPhase("error"); return; }
        window.history.replaceState({}, "", "/admin/reset-password");
        setPhase("ready");
        return;
      }

      // 4) Fall back: wait for PASSWORD_RECOVERY event
      const { data: sub } = supabase.auth.onAuthStateChange((event) => {
        if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
          setPhase("ready");
        }
      });

      // Give it a moment then declare expired/invalid
      setTimeout(() => {
        if (cancelled) return;
        supabase.auth.getSession().then(({ data: d }) => {
          if (cancelled) return;
          if (!d.session) {
            setErrorMsg("Reset link expired or invalid. Please request a new one.");
            setPhase("error");
          }
        });
      }, 2500);

      return () => sub?.subscription?.unsubscribe?.();
    }

    init();
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setPhase("submitting");
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setPhase("done");
      toast.success("Password updated. Redirecting...");
      setTimeout(() => navigate("/admin", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatSupabaseError(err));
      setPhase("ready");
    }
  };

  return (
    <main className="min-h-[100svh] bg-cream flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="rounded-3xl bg-white border border-ink/5 shadow-md p-8 sm:p-10" data-testid="reset-password-card">
          <div className="flex items-center gap-2.5 text-terracotta">
            <KeyRound size={18} /><span className="text-xs uppercase tracking-[0.18em] font-semibold">Set new password</span>
          </div>

          {phase === "verifying" && (
            <p className="mt-6 text-sm text-ink/60" data-testid="reset-verifying">Verifying your reset link...</p>
          )}

          {phase === "error" && (
            <>
              <h1 className="mt-3 font-display text-2xl font-semibold text-ink">Link expired</h1>
              <p className="mt-2 text-sm text-ink/70">{errorMsg || "This password reset link is no longer valid."}</p>
              <Button
                onClick={() => navigate("/admin/login")}
                className="mt-8 w-full rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12"
                data-testid="reset-back-to-login"
              >
                Back to sign in
              </Button>
            </>
          )}

          {phase === "done" && (
            <div className="mt-6 flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-sage text-white flex items-center justify-center"><CheckCircle2 size={24} /></div>
              <p className="mt-4 font-display text-xl text-ink">Password updated</p>
              <p className="mt-1 text-sm text-ink/60">Taking you to the admin dashboard...</p>
            </div>
          )}

          {(phase === "ready" || phase === "submitting") && (
            <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="reset-form">
              <h1 className="font-display text-2xl font-semibold text-ink">Choose a new password</h1>
              <p className="text-sm text-ink/60 -mt-3">At least 8 characters. Use something you'll remember — or a password manager.</p>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input id="new-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl h-11" data-testid="reset-new-password" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input id="confirm-password" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-xl h-11" data-testid="reset-confirm-password" />
              </div>
              <Button
                type="submit"
                disabled={phase === "submitting"}
                className="w-full rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12 shadow-md"
                data-testid="reset-submit"
              >
                <Lock size={16} className="mr-1.5" />
                {phase === "submitting" ? "Updating..." : "Update password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
